/**
 * NsaveCardLanding — Arabic RTL landing page for the nsave USD card.
 *
 * Route:  /eg/usd-card  (+ /travel, /freelancers, /online-payments, /business
 *         sub-paths that swap the Hero variant via URL). Also honours
 *         ?variant= and ?utm_content= for ad-driven message match.
 *
 * The page is a self-contained conversion asset per the approved brief:
 *   1. Product-led Hero anchored on the card (not the account).
 *   2. Value strip + Problem framing → 4 pillars.
 *   3. Prominent USD-only-deduction explanation.
 *   4. Use-cases, How-it-works, Speed & access split.
 *   5. Audience, Life-stage journey.
 *   6. Fees section — pricing DELIBERATELY not published as raw numbers here
 *      (all monetary values are gated behind approval). We link to the
 *      official fees page instead — safest until legal/pricing sign-off.
 *   7. Eligibility, Trust, FAQ, Final CTA, Sticky Mobile CTA, Footer.
 *
 * Copy is verbatim Egyptian Arabic — do NOT rewrite. Approved-content gates
 * (fee %, legal, KYC copy) are rendered as neutral placeholders that read
 * cleanly ("راجع الرسوم داخل حسابك") — no unapproved figures are printed.
 *
 * CTA URLs are `#signup` / `#existing` placeholders so the page can be
 * previewed independently. The user will wire real nsave URLs post-approval.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Globe, ShoppingBag, Store, Zap, ArrowLeft, ChevronDown, Menu, X,
  ShieldCheck, CheckCircle2, CreditCard, Plane, Briefcase, Laptop2,
  ArrowRight, Info, Lock, Wallet,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Config — swap-in points for approved variables & CTA URLs.
// The variable-slot approach mirrors the brief; keeping them local + typed
// makes the "gate un-approved copy" logic in JSX below trivial.
// ---------------------------------------------------------------------------
const CFG = {
  NEW_USER_CARD_SIGNUP_URL: "#signup",
  EXISTING_USER_CARD_REQUEST_URL: "#existing-user",
  LOGIN_URL: "#login",
  CARD_FEES_URL: "#fees",
  CARD_SUPPORT_URL: "#support",
  CARD_TERMS_URL: "#terms",
  PRIVACY_URL: "#privacy",
  NSAVE_LOGO_URL: "https://customer-assets-lxgj4vgw.emergentagent.net/job_charisma-protocol/artifacts/5f8wx7v5_FB_IMG_1784748401801.jpg",
  HERO_MOBILE_URL: "https://customer-assets-lxgj4vgw.emergentagent.net/job_charisma-protocol/artifacts/o5gdlo1n_file_0000000096e48246a4e997b6f4271817.png",
  HERO_DESKTOP_URL: "https://customer-assets-lxgj4vgw.emergentagent.net/job_charisma-protocol/artifacts/r9tpbk7g_file_0000000027848243a794704ffede191b.png",
};

// ---------------------------------------------------------------------------
// Hero variants — one per approved campaign. Default is the Card hero.
// The path/param → key mapping is done in `resolveVariant()` below.
// ---------------------------------------------------------------------------
const HERO_VARIANTS = {
  default: {
    eyebrow: "كارت nsave الدولي",
    headline: "كارت بالدولار يشتغل أونلاين وفي المحلات، في أكتر من 170 دولة.",
    para: "ادفع بأي عملة مباشرة من رصيدك بالدولار على nsave، للسفر، اشتراكات الشغل، والمشتريات الدولية، برسوم واضحة ومن غير لفة التحويل كل مرة.",
  },
  freelancer: {
    eyebrow: "للفريلانسرز والـ Remote Workers",
    headline: "قبضك بالدولار. ادفع بيه مباشرة.",
    para: "استقبل دخلك على nsave واستخدم رصيدك بالدولار في اشتراكات الشغل، الأدوات الأونلاين، السفر، والمشتريات اليومية بالكارت.",
  },
  online_spending: {
    eyebrow: "للمشتريات والاشتراكات الدولية",
    headline: "اشتراكاتك الدولية ماتقفش عند حدود كارت محلي.",
    para: "ادفع أونلاين من رصيدك بالدولار على nsave، على المواقع والمنصات التي تقبل شبكة الكارت، برسوم واضحة قبل الاستخدام.",
  },
  travel: {
    eyebrow: "كارتك معاك في السفر",
    headline: "سافر ودولاراتك جاهزة للاستخدام.",
    para: "ادفع في الفنادق، المطاعم، المتاجر، والمواقع أثناء سفرك مباشرة من رصيدك بالدولار على nsave.",
  },
  business: {
    eyebrow: "لأصحاب الـ Agencies والبيزنس الرقمي",
    headline: "دخل البيزنس بالدولار. ومصاريفه على نفس الكارت.",
    para: "استخدم رصيدك بالدولار في أدوات الشغل، الاشتراكات، المشتريات، والمصاريف الدولية من خلال كارت nsave.",
  },
};

const SUBPATH_TO_VARIANT = {
  travel: "travel",
  freelancers: "freelancer",
  "online-payments": "online_spending",
  business: "business",
};

const resolveVariant = (subpath, search) => {
  if (subpath && SUBPATH_TO_VARIANT[subpath]) return SUBPATH_TO_VARIANT[subpath];
  const q = new URLSearchParams(search);
  const v = q.get("variant") || q.get("utm_content") || q.get("utm_campaign");
  if (!v) return "default";
  const norm = v.toLowerCase();
  if (norm.includes("freelancer")) return "freelancer";
  if (norm.includes("online")) return "online_spending";
  if (norm.includes("travel")) return "travel";
  if (norm.includes("business")) return "business";
  return "default";
};

// ---------------------------------------------------------------------------
// Analytics — fire-and-forget beacon. Kept intentionally simple: the real
// integration will replace `window.dataLayer.push` and `window.ttq.track`
// in production. Never blocks a click.
// ---------------------------------------------------------------------------
const track = (event, props = {}) => {
  try {
    if (typeof window === "undefined") return;
    const payload = { event, ...props, ts: Date.now() };
    if (window.dataLayer && typeof window.dataLayer.push === "function") {
      window.dataLayer.push(payload);
    }
    if (process.env.NODE_ENV !== "production" && typeof console !== "undefined") {
      console.debug("[nsave]", payload);
    }
  } catch (_) { /* never blocks */ }
};

// Preserves any UTM / partner / referral params on every outbound CTA click.
const preserveParams = (baseUrl) => {
  try {
    if (typeof window === "undefined") return baseUrl;
    const inbound = new URLSearchParams(window.location.search);
    const keep = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ref", "partner"];
    const out = new URLSearchParams();
    keep.forEach((k) => { const v = inbound.get(k); if (v) out.set(k, v); });
    if (!out.toString()) return baseUrl;
    const glue = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${glue}${out.toString()}`;
  } catch (_) { return baseUrl; }
};

// ---------------------------------------------------------------------------
// Small presentational atoms — kept inline to avoid extra files.
// ---------------------------------------------------------------------------
const Eyebrow = ({ children }) => (
  <div className="text-[11px] sm:text-[12px] tracking-[0.22em] font-bold text-[#2563EB] uppercase">
    {children}
  </div>
);

const H2 = ({ children, className = "" }) => (
  <h2 className={`text-[26px] sm:text-[34px] lg:text-[40px] font-extrabold text-[#0B1220] leading-[1.25] tracking-tight ${className}`}>
    {children}
  </h2>
);

const CTA = ({ href, children, primary = true, dataTestid, onClick }) => {
  const handle = (e) => { onClick?.(e); };
  return (
    <a
      href={preserveParams(href)}
      onClick={handle}
      data-testid={dataTestid}
      className={
        primary
          ? "inline-flex items-center justify-center gap-2 rounded-full bg-[#ED1C24] hover:bg-[#D01820] active:scale-[0.99] text-white font-bold text-[15px] px-6 py-3.5 min-h-[52px] shadow-[0_10px_28px_-10px_rgba(237,28,36,0.55)] transition-all"
          : "inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#F1F5FF] active:scale-[0.99] text-[#0B1220] font-bold text-[14px] px-5 py-3 min-h-[48px] border border-[#D6DEEC] transition-all"
      }
    >
      {children}
    </a>
  );
};

const Card = ({ children, className = "", testid }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl bg-white border border-[#E5E9F2] shadow-[0_2px_16px_-8px_rgba(11,18,32,0.08)] p-6 ${className}`}
  >
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Main Landing Page
// ---------------------------------------------------------------------------
export default function NsaveCardLanding() {
  const { subpath } = useParams();
  const location = useLocation();
  const variantKey = useMemo(() => resolveVariant(subpath, location.search), [subpath, location.search]);
  const hero = HERO_VARIANTS[variantKey] || HERO_VARIANTS.default;
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);

  // Load IBM Plex Sans Arabic once — chosen for its clean fintech tone.
  useEffect(() => {
    const id = "nsave-font-link";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@300;400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  // SEO — set title & meta description (variant is used as headline source).
  useEffect(() => {
    document.title = "كارت دولاري للمشتريات الدولية من مصر | nsave";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = "استخدم كارت nsave للدفع أونلاين وفي المحلات في أكتر من 170 دولة، مباشرة من رصيدك بالدولار، مع رسوم واضحة قبل الاستخدام.";
  }, []);

  // Reveal the sticky mobile CTA once the user scrolls past the hero.
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track initial page view + selected variant.
  useEffect(() => { track("card_landing_view", { variant: variantKey }); }, [variantKey]);

  const NAV = [
    { href: "#features", label: "المزايا" },
    { href: "#how-it-works", label: "بيشتغل إزاي؟" },
    { href: "#use-cases", label: "استخدامات الكارت" },
    { href: "#fees", label: "الرسوم" },
    { href: "#faq", label: "الأسئلة الشائعة" },
  ];

  return (
    <div dir="rtl" lang="ar" className="min-h-screen bg-[#F7F9FC] text-[#0B1220]" style={{ fontFamily: "'IBM Plex Sans Arabic', system-ui, sans-serif" }} data-testid="nsave-card-landing">
      {/* ================= HEADER ================= */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E5E9F2]" data-testid="nsave-header">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 h-[56px] sm:h-[62px] flex items-center justify-between gap-2">
          <a href="#hero" className="flex items-center gap-2 shrink-0" data-testid="nsave-logo">
            <img src={CFG.NSAVE_LOGO_URL} alt="nsave" className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg object-cover" />
            <span className="font-extrabold text-[15px] sm:text-[17px] text-[#0B1220] tracking-tight">nsave</span>
          </a>

          <nav className="hidden lg:flex items-center gap-7 text-[13.5px] font-semibold text-[#3B4A63]">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="hover:text-[#0B1220] transition-colors">{n.label}</a>
            ))}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <a href={preserveParams(CFG.LOGIN_URL)} onClick={() => track("card_login_click", { location: "header" })} className="hidden md:inline text-[13px] font-semibold text-[#3B4A63] hover:text-[#0B1220] transition-colors" data-testid="nsave-header-login">تسجيل الدخول</a>
            {/* Desktop CTA — full label */}
            <a
              href={preserveParams(CFG.NEW_USER_CARD_SIGNUP_URL)}
              onClick={() => track("card_header_signup_click", {})}
              data-testid="nsave-header-signup"
              className="hidden md:inline-flex items-center justify-center gap-2 rounded-full bg-[#ED1C24] hover:bg-[#D01820] active:scale-[0.99] text-white font-bold text-[14px] px-5 py-2.5 min-h-[44px] shadow-[0_8px_22px_-10px_rgba(237,28,36,0.55)] transition-all whitespace-nowrap"
            >
              افتح حسابك واطلب الكارت
              <ArrowLeft className="w-4 h-4" />
            </a>
            {/* Mobile CTA — compact label */}
            <a
              href={preserveParams(CFG.NEW_USER_CARD_SIGNUP_URL)}
              onClick={() => track("card_header_signup_click", { device: "mobile" })}
              data-testid="nsave-header-signup-mobile"
              className="md:hidden inline-flex items-center justify-center gap-1.5 rounded-full bg-[#ED1C24] hover:bg-[#D01820] active:scale-[0.99] text-white font-bold text-[12.5px] px-3.5 py-2 min-h-[38px] shadow-[0_6px_18px_-10px_rgba(237,28,36,0.55)] transition-all whitespace-nowrap"
            >
              اطلب الكارت
            </a>
            <button onClick={() => setMenuOpen(v => !v)} className="lg:hidden inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-lg border border-[#E5E9F2] text-[#0B1220]" data-testid="nsave-menu-toggle" aria-label="Menu">
              {menuOpen ? <X className="w-4.5 h-4.5" /> : <Menu className="w-4.5 h-4.5" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-[#E5E9F2] bg-white" data-testid="nsave-mobile-menu">
            <div className="px-4 py-4 grid gap-2">
              {NAV.map((n) => (
                <a key={n.href} href={n.href} onClick={() => setMenuOpen(false)} className="py-2.5 text-[14px] font-semibold text-[#0B1220] border-b border-[#F1F5FF] last:border-b-0">{n.label}</a>
              ))}
              <a href={preserveParams(CFG.LOGIN_URL)} onClick={() => { setMenuOpen(false); track("card_login_click", { location: "mobile_menu" }); }} className="mt-2 py-2.5 text-[14px] font-semibold text-[#2563EB]">تسجيل الدخول</a>
            </div>
          </div>
        )}
      </header>

      {/* ================= HERO ================= */}
      <section id="hero" className="relative overflow-hidden bg-white border-b border-[#E5E9F2]" data-testid="nsave-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16 lg:py-20 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <Eyebrow>{hero.eyebrow}</Eyebrow>
            <h1 className="mt-4 text-[30px] sm:text-[42px] lg:text-[52px] font-extrabold leading-[1.2] tracking-tight text-[#0B1220]" data-testid="nsave-hero-title">
              {hero.headline}
            </h1>
            <p className="mt-5 text-[15px] sm:text-[17px] leading-[1.85] text-[#3B4A63] max-w-xl" data-testid="nsave-hero-para">
              {hero.para}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <CTA href={CFG.NEW_USER_CARD_SIGNUP_URL} dataTestid="nsave-hero-signup" onClick={() => track("card_hero_signup_click", { variant: variantKey })}>
                افتح حسابك واطلب الكارت
                <ArrowLeft className="w-4 h-4" />
              </CTA>
              <CTA href="#how-it-works" primary={false} dataTestid="nsave-hero-how-it-works" onClick={() => track("card_hero_how_it_works_click", {})}>
                شوف الكارت بيشتغل إزاي
                <ChevronDown className="w-4 h-4" />
              </CTA>
            </div>

            <a href={preserveParams(CFG.EXISTING_USER_CARD_REQUEST_URL)} onClick={() => track("card_hero_existing_user_click", {})} className="mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#2563EB] hover:underline" data-testid="nsave-hero-existing">
              عندك حساب nsave؟ اطلب الكارت
              <ArrowLeft className="w-3.5 h-3.5" />
            </a>

            <div className="mt-6 text-[12px] text-[#6C7A93] max-w-md leading-relaxed">
              توافر الكارت والمزايا يعتمد على بلد الإقامة، الأهلية، ونوع الحساب.
            </div>
          </div>

          <div className="relative">
            <picture>
              <source media="(min-width: 1024px)" srcSet={CFG.HERO_DESKTOP_URL} />
              <img src={CFG.HERO_MOBILE_URL} alt="كارت nsave — دفع ناجح في متجر ZARA بتحويل من EUR إلى USD" className="w-full h-auto rounded-2xl" data-testid="nsave-hero-image" loading="eager" />
            </picture>
            <div className="mt-3 text-[11px] text-[#8A97AE] text-center">البيانات المعروضة للتوضيح فقط.</div>
          </div>
        </div>
      </section>

      {/* ================= VALUE STRIP ================= */}
      <section className="bg-white border-b border-[#E5E9F2]" data-testid="nsave-value-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
          <ul className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
            {[
              { icon: Globe, label: "أونلاين وفي المحلات" },
              { icon: Globe, label: "أكتر من 170 دولة" },
              { icon: CreditCard, label: "الدفع بأي عملة" },
              { icon: Wallet, label: "الخصم من رصيدك بالدولار" },
              { icon: Info, label: "رسوم واضحة" },
            ].map((v, i) => (
              <li key={i} className={`flex items-center gap-2 bg-[#F1F5FF] rounded-full px-3 py-2 sm:px-4 sm:py-2.5 sm:justify-center ${i === 4 ? "col-span-2 sm:col-span-1 justify-center" : ""}`}>
                <v.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#2563EB] shrink-0" strokeWidth={2.4} />
                <span className="text-[11.5px] sm:text-[13px] font-semibold text-[#0B1220] leading-tight">{v.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ================= PROBLEM  ==============
          Removed — folded into the USD balance explainer directly below
          the Value Strip to shorten the page. ================================ */}

      {/* ================= CORE FEATURES ================= */}
      <section id="features" className="bg-white py-14 sm:py-20 border-b border-[#E5E9F2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Eyebrow>كارت واحد لاستخداماتك اليومية والدولية</Eyebrow>
          <H2 className="mt-3 max-w-3xl">ادفع من رصيدك بالدولار، بالطريقة اللي تناسبك.</H2>
          <p className="mt-4 text-[15px] leading-[1.9] text-[#3B4A63] max-w-3xl">
            كارت nsave مربوط برصيدك بالدولار. استخدمه في المشتريات الأونلاين، في المحلات، أثناء السفر، أو في مصاريف الشغل، على الأماكن التي تقبل شبكة الكارت.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Laptop2, label: "Online", title: "ادفع على المواقع والمنصات الدولية", body: "استخدم الكارت في اشتراكات الشغل، البرامج، الخدمات الرقمية، الحجوزات، والمشتريات الأونلاين التي تقبل شبكة الكارت." },
              { icon: Store, label: "In-store", title: "ادفع في المحلات مباشرة", body: "استخدم الكارت في نقاط البيع والمتاجر المؤهلة، داخل بلدك أو أثناء السفر، حسب توافر الخدمة وقبول شبكة الكارت." },
              { icon: Globe, label: "Global", title: "استخدمه في أكتر من 170 دولة", body: "ادفع بعملات مختلفة من خلال رصيدك بالدولار، من غير ما تحتاج تجهّز كل عملة قبل السفر أو الشراء." },
              { icon: Zap, label: "Instant access", title: "استخدم رصيدك من غير انتظار تحويل محلي", body: "لو رصيدك موجود على nsave، استخدمه بالكارت مباشرة بدل ما تنقله لحساب تاني وتستنى وصوله قبل الدفع." },
            ].map((f, i) => (
              <Card key={i} testid={`nsave-feature-${i}`} className="hover:shadow-[0_10px_28px_-14px_rgba(11,18,32,0.18)] transition-shadow">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F1F5FF] px-2.5 py-1 text-[10px] tracking-[0.18em] font-bold text-[#2563EB]" dir="ltr">{f.label}</div>
                <div className="mt-4 w-11 h-11 rounded-xl bg-[#0B1220] text-white flex items-center justify-center"><f.icon className="w-5 h-5" strokeWidth={2.2} /></div>
                <div className="mt-4 font-bold text-[15.5px] text-[#0B1220] leading-snug">{f.title}</div>
                <p className="mt-2 text-[13.5px] leading-[1.9] text-[#3B4A63]">{f.body}</p>
              </Card>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <CTA href={CFG.NEW_USER_CARD_SIGNUP_URL} dataTestid="nsave-features-signup" onClick={() => track("card_feature_signup_click", {})}>
              افتح حسابك واطلب الكارت
              <ArrowLeft className="w-4 h-4" />
            </CTA>
            <p className="text-[13px] text-[#6C7A93] leading-relaxed max-w-md">
              ابدأ بحساب nsave، كمّل التحقق من الهوية، واطلب الكارت لو متاح لحسابك.
            </p>
          </div>
        </div>
      </section>

      {/* ================= USD BALANCE EXPLANATION ================= */}
      <section className="bg-[#EAF2FF] py-14 sm:py-18 border-b border-[#D6E1F5]" data-testid="nsave-usd-explanation">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
          <div>
            <Eyebrow>مهم تعرف</Eyebrow>
            <H2 className="mt-3">الكارت بيتخصم من رصيدك بالدولار.</H2>
            <div className="mt-5 space-y-3.5 text-[15px] leading-[1.9] text-[#0B1220]">
              <p>حتى لو عندك أرصدة بـ GBP أو EUR على nsave، مشتريات الكارت بتتخصم من رصيد USD.</p>
              <p>لو دفعت بالدولار، يتم خصم قيمة العملية من رصيدك بالدولار.</p>
              <p>لو دفعت باليورو، الجنيه الإسترليني، أو أي عملة تانية، يتم تحويل قيمة العملية إلى الدولار، وبعدها يتم خصمها من رصيد USD مع تطبيق سعر الصرف والرسوم المعلنة.</p>
            </div>
            <div className="mt-6 text-[13px] text-[#3B4A63] leading-relaxed">
              راجع سعر الصرف والرسوم المطبقة على العملية من خلال حسابك أو صفحة الرسوم.{" "}
              <a href={preserveParams(CFG.CARD_FEES_URL)} onClick={() => track("card_fees_click", { location: "usd_explanation" })} className="text-[#2563EB] font-semibold hover:underline">راجع رسوم الكارت</a>
            </div>
          </div>
          <Card className="bg-white/90 backdrop-blur">
            <div className="text-[11px] tracking-[0.20em] font-bold text-[#2563EB] mb-4">FLOW</div>
            {[
              { label: "المتجر يطلب EUR", pill: "€120.00" },
              { label: "قيمة العملية تتحول إلى USD", pill: "€120.00 → $129.84" },
              { label: "يتم الخصم من رصيدك بالدولار", pill: "USD $129.84" },
            ].map((step, i, arr) => (
              <div key={i}>
                <div className="flex items-center justify-between gap-3 py-3">
                  <div className="text-[13.5px] font-semibold text-[#0B1220]">{step.label}</div>
                  <div className="text-[12.5px] font-bold text-[#0B1220] bg-[#F1F5FF] px-2.5 py-1 rounded-md tabular-nums" dir="ltr">{step.pill}</div>
                </div>
                {i < arr.length - 1 && <div className="flex justify-center py-1"><ChevronDown className="w-4 h-4 text-[#2563EB]" /></div>}
              </div>
            ))}
          </Card>
        </div>
      </section>

      {/* ================= USE CASES ================= */}
      <section id="use-cases" className="bg-white py-14 sm:py-20 border-b border-[#E5E9F2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Eyebrow>هتستخدم الكارت في إيه؟</Eyebrow>
          <H2 className="mt-3 max-w-3xl">من مصاريف الشغل للسفر، من غير ما تغيّر طريقة دفعك كل مرة.</H2>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {[
              { icon: Laptop2, title: "اشتراكات وأدوات الشغل", body: "ادفع مقابل البرامج، أدوات التصميم، الاستضافة، الإعلانات، منصات العمل، والخدمات الرقمية المستخدمة في شغلك.", tags: ["Software", "Hosting", "Ads", "Freelance platforms"] },
              { icon: Plane, title: "السفر والحجوزات", body: "استخدم الكارت في حجوزات الطيران والفنادق، المشتريات، المطاعم، والمصاريف المؤهلة أثناء السفر.", tags: ["Flights", "Hotels", "Shopping", "Restaurants"] },
              { icon: ShoppingBag, title: "المشتريات الأونلاين", body: "اشتري من المواقع والمتاجر الدولية التي تقبل شبكة الكارت، وادفع بأي عملة من خلال رصيدك بالدولار.", tags: ["Online stores", "Subscriptions", "Digital services", "Bookings"] },
              { icon: Briefcase, title: "مصاريف البيزنس", body: "ادفع اشتراكات الشركة، الأدوات، الخدمات، والحجوزات الدولية المستخدمة في تشغيل الـ Agency أو البيزنس الرقمي.", tags: ["Business tools", "Cloud services", "Marketing tools", "Travel expenses"] },
            ].map((u, i) => (
              <Card key={i} testid={`nsave-use-case-${i}`} className="h-full">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-[#F1F5FF] text-[#2563EB] flex items-center justify-center"><u.icon className="w-5 h-5" strokeWidth={2.2} /></div>
                  <div>
                    <div className="font-bold text-[16px] text-[#0B1220]">{u.title}</div>
                    <p className="mt-1.5 text-[13.5px] leading-[1.85] text-[#3B4A63]">{u.body}</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5" dir="ltr">
                  {u.tags.map((t) => (
                    <span key={t} className="text-[11px] font-semibold bg-[#F7F9FC] border border-[#E5E9F2] text-[#3B4A63] px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how-it-works" className="bg-[#F7F9FC] py-14 sm:py-20 border-b border-[#E5E9F2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Eyebrow>الكارت بيشتغل إزاي؟</Eyebrow>
          <H2 className="mt-3 max-w-3xl">من حسابك لأول عملية في أربع خطوات.</H2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: "1", title: "افتح حساب nsave", body: "سجّل بياناتك الأساسية وابدأ إنشاء حسابك." },
              { n: "2", title: "كمّل التحقق من الهوية", body: "قدّم البيانات والمستندات المطلوبة لتحديد أهليتك والخدمات المتاحة لحسابك." },
              { n: "3", title: "أضف رصيد بالدولار", body: "استقبل أو حوّل الأموال إلى رصيد USD على حسابك باستخدام الطرق المتاحة لك." },
              { n: "4", title: "اطلب الكارت وابدأ الاستخدام", body: "بعد تفعيل الكارت، استخدمه أونلاين أو في المحلات. يتم خصم مشترياتك من رصيدك بالدولار." },
            ].map((s, i) => (
              <Card key={i} testid={`nsave-step-${i}`} className="relative">
                <div className="text-[14px] font-extrabold text-white bg-[#ED1C24] w-8 h-8 rounded-full flex items-center justify-center tabular-nums" dir="ltr">{s.n}</div>
                <div className="mt-4 font-bold text-[15.5px] text-[#0B1220]">{s.title}</div>
                <p className="mt-2 text-[13.5px] leading-[1.85] text-[#3B4A63]">{s.body}</p>
              </Card>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <CTA href={CFG.NEW_USER_CARD_SIGNUP_URL} dataTestid="nsave-how-it-works-signup" onClick={() => track("card_how_it_works_signup_click", {})}>
              افتح حسابك واطلب الكارت
              <ArrowLeft className="w-4 h-4" />
            </CTA>
            <a href={preserveParams(CFG.EXISTING_USER_CARD_REQUEST_URL)} onClick={() => track("card_hero_existing_user_click", { location: "how_it_works" })} className="text-[13.5px] font-semibold text-[#2563EB] hover:underline">
              عندك حساب بالفعل؟ اطلب الكارت
            </a>
          </div>
        </div>
      </section>

      {/* ================= SPEED & ACCESS SPLIT ================
          Removed — content is redundant with Feature #4 "Instant access"
          in the CORE FEATURES section above. ================================ */}

      {/* ================= AUDIENCE ================
          Removed — audiences are already implicit in the four USE-CASE
          cards (freelancers → software, travelers → flights, entrepreneurs →
          business tools, tech → online stores). Kept the page focused. ====== */}

      {/* ================= LIFE-STAGE NARRATIVE ================
          Removed to shorten the page. Not essential for a card-acquisition
          landing that already covers who + when + how. ========================= */}

      {/* ================= FEES ================= */}
      <section id="fees" className="bg-[#0B1220] text-white py-14 sm:py-20 border-b border-[#0B1220]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Eyebrow><span className="text-[#8AA9FF]">الرسوم</span></Eyebrow>
          <H2 className="mt-3 text-white">اعرف تكلفة العملية قبل ما تستخدم الكارت.</H2>
          <p className="mt-4 text-[15px] leading-[1.9] text-white/80 max-w-3xl">
            تسعير الكارت لازم يكون بسيط وواضح. تفاصيل الرسوم الرسمية بتظهرلك داخل حسابك وقت الاستخدام، مع سعر الصرف وأي رسوم FX أو cross-border معتمدة.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { t: "الدفع بالدولار (USD)", b: "قيمة العملية تتخصم مباشرة من رصيدك بالدولار." },
              { t: "الدفع بعملة مختلفة", b: "يتم تحويل قيمة العملية إلى USD، ثم تطبيق سعر الصرف والرسوم المعتمدة قبل الخصم." },
              { t: "رسوم إصدار / شهرية / استبدال", b: "تظهر داخل حسابك وعلى صفحة الرسوم الرسمية، فراجعها قبل طلب الكارت." },
            ].map((r, i) => (
              <div key={i} className="rounded-2xl bg-white/[0.05] border border-white/[0.12] p-6" data-testid={`nsave-fee-row-${i}`}>
                <div className="font-bold text-[15px] text-white">{r.t}</div>
                <p className="mt-2 text-[13.5px] leading-[1.85] text-white/75">{r.b}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href={preserveParams(CFG.CARD_FEES_URL)} onClick={() => track("card_fees_click", { location: "fees_section" })} className="inline-flex items-center gap-2 rounded-full bg-white text-[#0B1220] font-bold text-[14px] px-5 py-3 min-h-[48px] transition-all hover:bg-[#F1F5FF]" data-testid="nsave-fees-review">
              راجع كل رسوم الكارت
              <ArrowLeft className="w-4 h-4" />
            </a>
            <p className="text-[13px] text-white/70 max-w-md">القيمة النهائية تعتمد على سعر الصرف والرسوم وقت تنفيذ العملية.</p>
          </div>
        </div>
      </section>

      {/* ================= ELIGIBILITY ================= */}
      <section className="bg-white py-14 sm:py-20 border-b border-[#E5E9F2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <Eyebrow>متاح بإجراءات واضحة</Eyebrow>
            <H2 className="mt-3">ابدأ بإثبات هويتك، واعرف فوراً إذا الكارت متاح لحسابك.</H2>
            <p className="mt-4 text-[15px] leading-[1.9] text-[#3B4A63]">
              افتح حسابك على nsave، كمّل التحقق من الهوية، وبعد مراجعة الأهلية هتشوف أنواع الكروت والمزايا المتاحة لك.
            </p>
            <div className="mt-6 space-y-3">
              {[
                { icon: ShieldCheck, t: "تحقق من الهوية (KYC)", b: "لازم تكمل التحقق قبل ما تقدر تطلب الكارت." },
                { icon: CreditCard, t: "نوع الكارت", b: "الكارت متاح كـ Virtual و/أو Physical حسب أهليتك ونوع حسابك." },
                { icon: Globe, t: "الدول المتاحة", b: "توافر الكارت بيعتمد على بلد الإقامة وأي شروط تنظيمية سارية." },
              ].map((it, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#F7F9FC] border border-[#E5E9F2] rounded-xl p-4">
                  <div className="w-9 h-9 rounded-lg bg-white text-[#2563EB] border border-[#E5E9F2] flex items-center justify-center shrink-0"><it.icon className="w-4.5 h-4.5" strokeWidth={2.2} /></div>
                  <div>
                    <div className="font-bold text-[14.5px] text-[#0B1220]">{it.t}</div>
                    <p className="mt-1 text-[13px] leading-[1.85] text-[#3B4A63]">{it.b}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-7">
              <CTA href={CFG.NEW_USER_CARD_SIGNUP_URL} dataTestid="nsave-eligibility-signup" onClick={() => track("card_eligibility_signup_click", {})}>
                افتح حسابك وابدأ التحقق
                <ArrowLeft className="w-4 h-4" />
              </CTA>
            </div>
          </div>
          <Card className="bg-[#EAF2FF] border-[#D6E1F5]">
            <div className="text-[11px] tracking-[0.20em] font-bold text-[#2563EB]">CHECKLIST</div>
            <div className="mt-3 font-bold text-[16px] text-[#0B1220]">محتاج إيه عشان تطلب الكارت؟</div>
            <ul className="mt-4 space-y-2.5 text-[13.5px] text-[#0B1220]">
              {[
                "حساب nsave نشط.",
                "التحقق من الهوية مكتمل ومقبول.",
                "رصيد USD متاح للاستخدام.",
                "أهلية الكارت مؤكدة على حسابك.",
              ].map((c, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4.5 h-4.5 text-[#2563EB] mt-0.5 shrink-0" strokeWidth={2.5} /><span>{c}</span></li>
              ))}
            </ul>
            <div className="mt-4 text-[12.5px] text-[#3B4A63] leading-relaxed">
              التفاصيل النهائية لتوافر الكارت بتظهر داخل حسابك بعد اكتمال المراجعة.
            </div>
          </Card>
        </div>
      </section>

      {/* ================= TRUST ================
          Removed — trust cues (USD deduction source, fees clarity,
          eligibility, support link) are already present in the FEES,
          ELIGIBILITY and FAQ sections. Kept the page focused on conversion. */}

      {/* ================= FAQ ================= */}
      <section id="faq" className="bg-white py-14 sm:py-20 border-b border-[#E5E9F2]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Eyebrow>الأسئلة الشائعة</Eyebrow>
          <H2 className="mt-3">قبل ما تطلب الكارت، دي أهم الإجابات.</H2>
          <div className="mt-8 divide-y divide-[#E5E9F2] border border-[#E5E9F2] rounded-2xl bg-white overflow-hidden">
            {[
              { q: "كارت nsave بيشتغل فين؟", a: "يُستخدم الكارت في أكتر من 170 دولة، على المواقع والمتاجر ونقاط البيع التي تقبل شبكة الكارت، مع مراعاة أي قيود مرتبطة بالدولة أو التاجر أو نوع العملية." },
              { q: "الكارت بيتخصم من أنهي رصيد؟", a: "كل معاملات الكارت بتتخصم من رصيدك بالدولار USD، حتى لو عندك أرصدة بـ GBP أو EUR على حسابك." },
              { q: "إيه اللي بيحصل لو دفعت بعملة غير الدولار؟", a: "يتم تحويل قيمة العملية إلى الدولار، وبعدها يتم الخصم من رصيد USD. يتم تطبيق سعر الصرف وأي رسوم FX أو cross-border معتمدة وقت تنفيذ العملية." },
              { q: "رسوم الكارت كام؟", a: "تفاصيل الرسوم بتظهر داخل حسابك على صفحة الكارت الرسمية. راجع كل رسوم الكارت قبل الاستخدام لتحديد التكلفة النهائية على كل عملية." },
              { q: "هل الكارت متاح للمستخدمين في مصر؟", a: "توافر الكارت للمستخدمين في مصر بيعتمد على شروط الأهلية، اكتمال التحقق من الهوية، وأي متطلبات تنظيمية سارية. افتح حسابك وكمّل الخطوات وهتعرف فوراً هل الكارت متاح ليك." },
              { q: "محتاج إيه عشان أطلب الكارت؟", a: "افتح حساب nsave، كمّل التحقق من الهوية، وبعد مراجعة الأهلية هتقدر تشوف إذا الكارت متاح لحسابك وخطوات طلبه أو تفعيله." },
            ].map((f, i) => {
              const open = faqOpen === i;
              return (
                <div key={i}>
                  <button
                    onClick={() => { const nxt = open ? null : i; setFaqOpen(nxt); if (!open) track("card_faq_open", { index: i, q: f.q }); }}
                    className="w-full text-right flex items-center justify-between gap-4 py-4 px-5 hover:bg-[#F7F9FC] transition-colors"
                    aria-expanded={open}
                    data-testid={`nsave-faq-toggle-${i}`}
                  >
                    <span className="font-bold text-[14.5px] sm:text-[15px] text-[#0B1220] leading-snug">{f.q}</span>
                    <ChevronDown className={`w-4.5 h-4.5 text-[#2563EB] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 text-[13.5px] leading-[1.9] text-[#3B4A63]" data-testid={`nsave-faq-answer-${i}`}>
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="bg-[#2563EB] text-white py-14 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-[11px] tracking-[0.28em] font-bold text-white/70 uppercase">كارت nsave</div>
          <h2 className="mt-4 text-[30px] sm:text-[42px] font-extrabold leading-[1.2]" data-testid="nsave-final-title">
            دولاراتك موجودة.<br />استخدمها.
          </h2>
          <p className="mt-5 text-[15px] sm:text-[16px] leading-[1.85] text-white/90 max-w-2xl mx-auto">
            افتح حسابك على nsave، كمّل التحقق من الهوية، واطلب الكارت عشان تدفع أونلاين وفي المحلات من رصيدك بالدولار.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href={preserveParams(CFG.NEW_USER_CARD_SIGNUP_URL)} onClick={() => track("card_final_signup_click", {})} className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0B1220] font-bold text-[15px] px-6 py-3.5 min-h-[52px] hover:bg-[#F1F5FF] transition-all shadow-[0_10px_28px_-10px_rgba(0,0,0,0.35)]" data-testid="nsave-final-signup">
              افتح حسابك واطلب الكارت
              <ArrowLeft className="w-4 h-4" />
            </a>
            <a href={preserveParams(CFG.EXISTING_USER_CARD_REQUEST_URL)} onClick={() => track("card_final_existing_user_click", {})} className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent border border-white/40 text-white font-bold text-[14px] px-5 py-3 min-h-[48px] hover:bg-white/10 transition-all" data-testid="nsave-final-existing">
              عندك حساب؟ اطلب الكارت
            </a>
          </div>
          <p className="mt-6 text-[12.5px] text-white/70 max-w-xl mx-auto">
            توافر الكارت والمزايا يعتمد على البلد، الأهلية، ونوع الحساب. راجع الرسوم والشروط قبل الاستخدام.
          </p>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="bg-[#0B1220] text-white/80 pt-12 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2.5">
              <img src={CFG.NSAVE_LOGO_URL} alt="nsave" className="w-9 h-9 rounded-lg object-cover" />
              <span className="font-extrabold text-[17px] text-white tracking-tight">nsave</span>
            </div>
            <p className="mt-4 text-[13px] leading-[1.85] text-white/70 max-w-xs">
              حساب وكارت لاستخدام دخلك بالدولار أونلاين، في المحلات، وأثناء السفر.
            </p>
          </div>
          <div>
            <div className="text-[11px] tracking-[0.22em] font-bold text-white/60 mb-3">الكارت</div>
            <ul className="space-y-2.5 text-[13.5px]">
              <li><a href="#features" className="hover:text-white">المزايا</a></li>
              <li><a href="#how-it-works" className="hover:text-white">بيشتغل إزاي؟</a></li>
              <li><a href="#use-cases" className="hover:text-white">استخدامات الكارت</a></li>
              <li><a href="#fees" className="hover:text-white">الرسوم</a></li>
              <li><a href={preserveParams(CFG.NEW_USER_CARD_SIGNUP_URL)} className="hover:text-white">اطلب الكارت</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] tracking-[0.22em] font-bold text-white/60 mb-3">المساعدة</div>
            <ul className="space-y-2.5 text-[13.5px]">
              <li><a href="#faq" className="hover:text-white">الأسئلة الشائعة</a></li>
              <li><a href={preserveParams(CFG.CARD_SUPPORT_URL)} className="hover:text-white">الدعم</a></li>
              <li><a href={preserveParams(CFG.CARD_TERMS_URL)} className="hover:text-white">شروط الكارت</a></li>
              <li><a href={preserveParams(CFG.PRIVACY_URL)} className="hover:text-white">سياسة الخصوصية</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] tracking-[0.22em] font-bold text-white/60 mb-3">معلومات مهمة</div>
            <p className="text-[12.5px] leading-[1.85] text-white/70">
              توافر الكارت، العملات، الدول، أنواع العمليات، طرق الشحن، والحدود يعتمد على الأهلية ونوع الحساب والشروط المتاحة وقت الاستخدام.
            </p>
            <p className="mt-3 text-[12.5px] leading-[1.85] text-white/70">
              يتم تمويل معاملات الكارت من رصيد USD. عند الدفع بعملة مختلفة، قد يتم تطبيق سعر صرف ورسوم معتمدة.
            </p>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-10 pt-6 border-t border-white/10 text-[12px] text-white/50 flex flex-wrap items-center gap-3 justify-between">
          <div>© {new Date().getFullYear()} nsave. جميع الحقوق محفوظة.</div>
          <div className="flex items-center gap-1.5" dir="ltr"><Lock className="w-3.5 h-3.5" /><span>Secured session</span></div>
        </div>
      </footer>

      {/* ================= STICKY MOBILE CTA ================= */}
      {showSticky && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E5E9F2] shadow-[0_-8px_28px_-10px_rgba(11,18,32,0.15)] px-4 py-3 flex items-center gap-3" data-testid="nsave-sticky-cta">
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-bold text-[#0B1220] truncate">كارت بالدولار أونلاين وفي المحلات</div>
            <a href={preserveParams(CFG.EXISTING_USER_CARD_REQUEST_URL)} onClick={() => track("card_mobile_sticky_click", { intent: "existing" })} className="text-[11.5px] text-[#2563EB] font-semibold">عندك حساب؟</a>
          </div>
          <a href={preserveParams(CFG.NEW_USER_CARD_SIGNUP_URL)} onClick={() => track("card_mobile_sticky_click", { intent: "signup" })} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#ED1C24] hover:bg-[#D01820] text-white font-bold text-[13.5px] px-4 py-2.5 min-h-[46px] shadow-[0_10px_20px_-10px_rgba(237,28,36,0.55)]" data-testid="nsave-sticky-signup">
            اطلب الكارت
            <ArrowRight className="w-4 h-4 rotate-180" />
          </a>
        </div>
      )}
      {/* Spacer so sticky bar never covers the footer / last CTA on mobile */}
      {showSticky && <div className="lg:hidden h-20" aria-hidden />}
    </div>
  );
}
